"use client"

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"
import { Grid2X2, List } from "lucide-react"
import { parseAsStringLiteral, useQueryStates } from "nuqs"
import { useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/utilities"
import { ProductCardSkeleton } from "~/components/shared/product-card/skeleton"
import { ProductCard } from "../../../shared/product-card"
import type { SortBy, SortOrder, ViewMode } from "../query-params/contract"
import {
  type CategoryProductsPageLoader,
  getCategoryProductsInfiniteQueryOptions,
} from "./query"

const listingTopbarParsers = {
  orderby: parseAsStringLiteral<SortBy>([
    "menu_order",
    "date",
    "popularity",
    "rating",
    "title",
  ]).withDefault("popularity"),
  order: parseAsStringLiteral<SortOrder>(["asc", "desc"]).withDefault("desc"),
  view: parseAsStringLiteral<ViewMode>(["grid", "list"]).withDefault("grid"),
}

const SORT_OPTIONS = [
  { label: "Mais relevantes", orderby: "menu_order", order: "asc" },
  { label: "Mais populares", orderby: "popularity", order: "desc" },
  { label: "Mais recentes", orderby: "date", order: "desc" },
  { label: "Melhor avaliados", orderby: "rating", order: "desc" },
  { label: "Nome (A-Z)", orderby: "title", order: "asc" },
  { label: "Nome (Z-A)", orderby: "title", order: "desc" },
] as const

const LOAD_MORE_ROOT_MARGIN = "400px 0px"
const GRID_SKELETON_COUNT = 6
const LIST_SKELETON_COUNT = 3

const getSortKey = (orderby: SortBy, order: SortOrder): string =>
  `${orderby}:${order}`

interface CategoryProductListProps {
  categoryName: string
  queryKeySeed: string
  onLoadMoreProducts: CategoryProductsPageLoader
}

export const CategoryProducstsList = ({
  categoryName = "Categoria",
  queryKeySeed,
  onLoadMoreProducts,
}: CategoryProductListProps) => {
  const [query, setQuery] = useQueryStates(listingTopbarParsers, {
    history: "replace",
    shallow: false,
  })

  const productsList = useRef<HTMLDivElement | null>(null)

  const selectedSortKey = getSortKey(query.orderby, query.order)
  const selectedView = query.view

  const onSortChange = (value: string): void => {
    const [orderby, order] = value.split(":") as [SortBy, SortOrder]
    setQuery({ orderby, order })
  }

  const onViewChange = (view: ViewMode): void => {
    setQuery({ view })
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isPending,
    isError,
  } = useInfiniteQuery({
    ...getCategoryProductsInfiniteQueryOptions({
      onLoadMoreProducts,
      queryKeySeed,
    }),
    placeholderData: keepPreviousData,
  })

  const products = data?.pages.flatMap((page) => page.items) ?? []
  const totalProducts = data?.pages[0]?.totalProducts ?? 0

  useEffect(() => {
    if (!(productsList.current && hasNextPage)) {
      return
    }
    let isCancelled = false

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isCancelled || !entry?.isIntersecting || isFetchingNextPage) {
          return
        }

        fetchNextPage()
      },
      { rootMargin: LOAD_MORE_ROOT_MARGIN }
    )

    observer.observe(productsList.current)

    return () => {
      isCancelled = true
      observer.disconnect()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  if (isError) {
    return (
      <div
        className='rounded-md border border-red-200 bg-red-50 p-8 text-center text-red-900 text-sm'
        role='alert'
      >
        Não foi possível carregar os produtos agora. Tente novamente mais tarde.
      </div>
    )
  }

  return (
    <section
      aria-busy={isPending || isFetching}
      className='space-y-4'
      id='category-products-section'
    >
      <header className='rounded-md border border-gray-200 bg-white p-4'>
        <h1 className='font-semibold text-gray-900 text-xl'>{categoryName}</h1>
        <div className='mt-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
          <p aria-live='polite' className='text-gray-600 text-sm'>
            {totalProducts} produtos encontrados
          </p>
          <div className='flex flex-wrap items-center gap-2'>
            <Select onValueChange={onSortChange} value={selectedSortKey}>
              <SelectTrigger aria-label='Ordenar produtos' className='w-54'>
                <SelectValue placeholder='Ordenar por' />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((option) => (
                  <SelectItem
                    key={getSortKey(option.orderby, option.order)}
                    value={getSortKey(option.orderby, option.order)}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <fieldset
              aria-label='Modo de visualizacao'
              className='inline-flex rounded-md border border-gray-200 p-1'
            >
              <Button
                aria-pressed={selectedView === "grid"}
                onClick={() => onViewChange("grid")}
                size='icon-sm'
                type='button'
                variant={selectedView === "grid" ? "default" : "ghost"}
              >
                <Grid2X2 className='h-4 w-4' />
                <span className='sr-only'>Visualizacao em grade</span>
              </Button>
              <Button
                aria-pressed={selectedView === "list"}
                onClick={() => onViewChange("list")}
                size='icon-sm'
                type='button'
                variant={selectedView === "list" ? "default" : "ghost"}
              >
                <List className='h-4 w-4' />
                <span className='sr-only'>Visualizacao em lista</span>
              </Button>
            </fieldset>
          </div>
        </div>
      </header>

      <output aria-atomic='true' aria-live='polite' className='sr-only'>
        {isPending && "Carregando produtos"}
        {isFetchingNextPage && "Carregando mais produtos"}
      </output>

      {isPending && (
        <div
          className={cn(
            "min-h-100",
            selectedView === "grid"
              ? "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
              : "space-y-3"
          )}
        >
          {Array.from({
            length:
              selectedView === "grid"
                ? GRID_SKELETON_COUNT
                : LIST_SKELETON_COUNT,
          }).map((_, index) => (
            <ProductCardSkeleton
              className='w-full'
              // biome-ignore lint/suspicious/noArrayIndexKey: Static placeholder list while loading.
              key={`product-skeleton-initial-${index}`}
            />
          ))}
        </div>
      )}

      {!isPending && products.length === 0 && (
        <div className='rounded-md border border-gray-300 border-dashed bg-white p-8 text-center text-gray-600 text-sm'>
          Nenhum produto encontrado para os filtros atuais.
        </div>
      )}

      {!isPending && products.length > 0 && (
        <div
          className={cn(
            "min-h-100 [contain-intrinsic-size:1200px] [content-visibility:auto]",
            selectedView === "grid"
              ? "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
              : "space-y-3"
          )}
        >
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
          {isFetchingNextPage &&
            Array.from({
              length:
                selectedView === "grid"
                  ? GRID_SKELETON_COUNT
                  : LIST_SKELETON_COUNT,
            }).map((_, index) => (
              <ProductCardSkeleton
                className='w-full'
                // biome-ignore lint/suspicious/noArrayIndexKey: Static placeholder list while loading.
                key={`product-skeleton-${index}`}
              />
            ))}
        </div>
      )}

      {products.length > 0 && hasNextPage && (
        <div className='flex flex-col items-center gap-3'>
          <div aria-hidden='true' className='h-2 w-full' ref={productsList} />
        </div>
      )}
    </section>
  )
}
